import { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';

// The registration UI uses a single international phone field (PhoneField) whose
// value is an E.164 string, e.g. "+4798765432". The backend + Authy still keep
// the country code and national number SEPARATE (User.CountryCode "+47" and
// IdentityUser.PhoneNumber "98765432"), so these helpers bridge the two shapes:
// split on submit, recombine when loading a user into the edit form.

/** Split an E.164 phone into the separate CountryCode ("+47") and national
 *  PhoneNumber ("98765432") the backend expects. Callers gate this with vPhone,
 *  so the input is normally valid; the fallback only guards against surprises. */
export function splitPhone(e164: string): { countryCode: string; phoneNumber: string } {
    try {
        const parsed = e164 ? parsePhoneNumber(e164) : undefined;
        if (parsed?.countryCallingCode && parsed?.nationalNumber) {
            return {
                countryCode: `+${parsed.countryCallingCode}`,
                phoneNumber: String(parsed.nationalNumber),
            };
        }
    } catch {
        // Unparseable — fall through to the best-effort result below.
    }
    return { countryCode: '', phoneNumber: '' };
}

/** Recombine a stored CountryCode + national PhoneNumber back into an E.164
 *  string for the PhoneField when editing. Tolerant of legacy/AD rows where the
 *  number may already embed the country code or carry a leading '+'. */
export function combinePhone(countryCode?: string, phoneNumber?: string): string {
    const raw = (phoneNumber ?? '').trim();
    // Legacy rows sometimes store the whole number in the phone field, using
    // either a leading '+' or the '00' international call prefix.
    if (raw.startsWith('+')) return raw.replace(/[^\d+]/g, '');
    if (raw.startsWith('00')) return `+${raw.slice(2).replace(/\D/g, '')}`;

    const cc = (countryCode ?? '').replace(/\D/g, '');
    const nn = raw.replace(/\D/g, '');
    if (!cc && !nn) return '';
    if (!cc) return `+${nn}`;
    if (!nn) return `+${cc}`;

    // Normal case: separate country code + national number. Trust it when the
    // join is a valid number...
    const joined = `+${cc}${nn}`;
    if (isValidPhoneNumber(joined)) return joined;
    // ...otherwise, for legacy rows that already embedded the code, prefer the
    // bare number when THAT parses cleanly.
    if (nn.startsWith(cc) && isValidPhoneNumber(`+${nn}`)) return `+${nn}`;
    return joined;
}

/** True when a country code + national number form a valid international phone
 *  number (any country). Lets the CSV import validate phones exactly as the
 *  single-user PhoneField does. */
export function isValidPhone(countryCode: string, phoneNumber: string): boolean {
    try {
        return isValidPhoneNumber(combinePhone(countryCode, phoneNumber));
    } catch {
        return false;
    }
}
